import React from "react";
import { useNavigate } from "react-router-dom";
import { Button, Container, Grid, Paper, Typography, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/CheckCircle";
import BuildIcon from "@mui/icons-material/Build";
import ReceiptIcon from "@mui/icons-material/Receipt";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
// Use the shared logo in src
import logo from "../logo 3.png";

const Feature = ({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) => (
  <Paper elevation={0} className="p-6 rounded-2xl h-full" sx={{ backgroundColor: (t)=>t.palette.background.paper, border: '1px solid', borderColor: 'divider' }}>
    <div className="flex items-start gap-3">
      <div className="mt-1 text-blue-500">{icon}</div>
      <div>
        <Typography variant="h6" fontWeight={900} gutterBottom>{title}</Typography>
        <Typography color="text.secondary">{desc}</Typography>
      </div>
    </div>
  </Paper>
);

export default function LandingPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '100vh', background: theme.palette.background.default }}>
      {/* Header */}
      <header
        className="px-4 md:px-10 py-3 md:py-4"
        style={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(180deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        }}
      >
        <div className="mx-auto flex items-center justify-between gap-3" style={{ maxWidth: 1440 }}>
          <div className="flex items-center gap-2 md:gap-4 min-w-0">
            <img
              src={logo}
              alt="RepairSync"
              style={{ objectFit: 'contain', filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.35))', height: 44 }}
              className="md:h-[72px]"
            />
            <Typography
              fontWeight={900}
              sx={{
                color: 'white',
                letterSpacing: 0.3,
                typography: { xs: 'h6', sm: 'h5', md: 'h4', xl: 'h3' },
              }}
            >
              RepairSync
            </Typography>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <Button size="small" onClick={() => navigate('/login')} color="inherit" sx={{ fontSize: { xs: '0.9rem', md: '1.05rem' }, fontWeight: 800 }}>
              Log in
            </Button>
            <Button size="small" variant="contained" onClick={() => navigate('/signup')} sx={{ fontSize: { xs: '0.9rem', md: '1.05rem' }, fontWeight: 900 }}>
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <Container maxWidth="xl" className="py-10 md:py-16">
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography
              gutterBottom
              sx={{
                color: 'white',
                fontWeight: 900,
                typography: { xs: 'h4', sm: 'h3', md: 'h2', xl: 'h1' },
                lineHeight: 1.05,
              }}
            >
              Run your repair shop smarter with RepairSync.
            </Typography>
            <Typography
              paragraph
              sx={{ color: 'rgba(255,255,255,0.85)', typography: { xs: 'subtitle1', md: 'h6' } }}
            >
              Tickets, inventory, invoices, POS and reporting — all in one fast, beautiful platform.
            </Typography>
            <div className="flex flex-wrap gap-2 md:gap-3 mt-2">
              <Button size="medium" variant="contained" onClick={() => navigate('/signup')}>Start Free</Button>
              <Button size="medium" variant="outlined" onClick={() => navigate('/login')}>Log in</Button>
            </div>
            <div className="flex flex-wrap gap-6 mt-6 text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
              <span className="flex items-center gap-1"><CheckIcon fontSize="small" color="success"/> No credit card</span>
              <span className="flex items-center gap-1"><CheckIcon fontSize="small" color="success"/> Multi-tenant</span>
              <span className="flex items-center gap-1"><CheckIcon fontSize="small" color="success"/> Dark mode</span>
            </div>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper elevation={0} className="rounded-2xl overflow-hidden flex items-center justify-center p-6 md:p-10" sx={{
              border: '1px solid', borderColor: 'divider',
              backgroundColor: (t)=>t.palette.background.paper,
            }}>
              <Box sx={{
                width: { xs: 200, md: 260 }, height: { xs: 200, md: 260 }, borderRadius: '50%',
                background: `radial-gradient( circle at 50% 50%, rgba(34,211,238,0.25), rgba(37,99,235,0.12) 55%, transparent 70%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <img src={logo} alt="RepairSync" style={{ objectFit: 'contain', filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.35))', height: 160 }} className="md:h-[200px]" />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Features */}
      <Container maxWidth="lg" className="pb-12 md:pb-16">
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid item xs={12} md={6} lg={3}><Feature icon={<BuildIcon/>} title="Tickets & Workflow" desc="Fast intake, status tracking, device details, diagnostics and notes."/></Grid>
          <Grid item xs={12} md={6} lg={3}><Feature icon={<ReceiptIcon/>} title="Invoices & POS" desc="Create invoices from tickets, take payments, print or email receipts."/></Grid>
          <Grid item xs={12} md={6} lg={3}><Feature icon={<SupportAgentIcon/>} title="Customers" desc="Profiles, contact info and history at a glance; notify when ready."/></Grid>
          <Grid item xs={12} md={6} lg={3}><Feature icon={<CheckIcon/>} title="Inventory" desc="Parts and accessories with accurate stock, suppliers and PO flow."/></Grid>
        </Grid>
      </Container>

      {/* CTA */}
      <div className="py-12" style={{ background: theme.palette.background.paper, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="lg" className="text-center">
          <Typography variant="h4" fontWeight={900} gutterBottom>
            Ready to get started?
          </Typography>
          <Typography color="text.secondary" paragraph>
            Create your shop in minutes and invite your team.
          </Typography>
          <Button size="large" variant="contained" onClick={() => navigate('/signup')}>Create Account</Button>
        </Container>
      </div>

      {/* Footer */}
      <footer className="py-8 text-center text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
        © 2025 RepairSync. All rights reserved.
      </footer>
    </div>
  );
}
